import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { useEffect } from "react";
import posthog from "@/lib/posthog";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HealthCheck } from "@/components/HealthCheck";
import { CookieConsent } from "@/components/CookieConsent";
import { ToastProvider } from "@/contexts/ToastContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Register from "./pages/Register";
import Auth from "./pages/Auth";
import VerifyEmailPending from "./pages/VerifyEmailPending";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvite from "./pages/AcceptInvite";
import CreateOrganization from "./pages/onboarding/CreateOrganization";
import ConnectDataSource from "./pages/onboarding/ConnectDataSource";
import SyncingData from "./pages/onboarding/SyncingData";
import BlinkApp from "./BlinkApp";
import QuickBooksCallback from "./pages/integrations/QuickBooksCallback";
import AuthError from "./pages/AuthError";
import TokenExpired from "./pages/TokenExpired";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import LicenseAgreement from "./pages/LicenseAgreement";

// Gates the main app behind Firebase Authentication (see AuthContext).
// Unauthenticated visitors are sent to /login; the auth state is checked
// once on load and kept in sync afterwards via onAuthStateChanged.
function CodeGate({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
}

// Redirect legacy /app/* URLs to the same path at the root (e.g. /app/finance/kpis → /finance/kpis)
function LegacyAppRedirect() {
  const location = useLocation();
  const target = location.pathname.replace(/^\/app\/?/, "/");
  return <Navigate to={target === "/" ? "/home" : target} replace />;
}

function PostHogPageview() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview", { $current_url: window.location.href });
  }, [location.pathname]);
  return null;
}

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ErrorBoundary component="App">
        <AuthProvider>
        <HealthCheck>
          <Router>
            <PostHogPageview />
            <ToastProvider>
            <Toaster />
            <Sonner />
            <CookieConsent />
            <Routes>
              {/* Entry — Firebase email/password auth, tabs for sign in / sign up */}
              <Route path="/login" element={<Auth />} />
              <Route path="/signup" element={<Auth />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />

              {/* Onboarding routes */}
              <Route path="/onboarding/organization" element={<ProtectedRoute><CreateOrganization /></ProtectedRoute>} />
              <Route path="/onboarding/connect" element={<ProtectedRoute><ConnectDataSource /></ProtectedRoute>} />
              <Route path="/onboarding/syncing" element={<ProtectedRoute><SyncingData /></ProtectedRoute>} />

              {/* Legacy routes → main app */}
              <Route path="/dashboard" element={<Navigate to="/home" replace />} />
              <Route path="/app/*" element={<LegacyAppRedirect />} />

              {/* Integration callbacks */}
              <Route path="/integrations/quickbooks/callback" element={<ProtectedRoute><QuickBooksCallback /></ProtectedRoute>} />

              {/* Error / utility routes */}
              <Route path="/auth/error" element={<AuthError />} />
              <Route path="/auth/expired" element={<TokenExpired />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/license-agreement" element={<LicenseAgreement />} />
              <Route path="/404" element={<NotFound />} />

              {/* Main app (BlinkBooks) — owns the root and all remaining routes */}
              <Route path="/*" element={<CodeGate><BlinkApp /></CodeGate>} />
            </Routes>
            </ToastProvider>
          </Router>
        </HealthCheck>
        </AuthProvider>
        </ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
